import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  CheckCircle, 
  LocalHospital,
  Schedule,
  Star
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Services: React.FC = () => {
  const theme = useTheme();
  const services = [
    {
      category: 'Ortodoncia',
      icon: '🦷',
      services: [
        {
          name: 'Brackets Metálicos',
          description: 'Tratamiento tradicional con brackets de acero inoxidable',
          duration: '18-24 meses',
          price: 'Desde $150.000',
          features: ['Ajustes mensuales', 'Incluye retenedores', 'Seguimiento completo']
        },
        {
          name: 'Brackets Estéticos',
          description: 'Brackets de cerámica o zafiro para una apariencia más discreta',
          duration: '18-24 meses',
          price: 'Desde $200.000',
          features: ['Aparato casi invisible', 'Material resistente', 'Resultados garantizados']
        },
        {
          name: 'Invisalign',
          description: 'Alineadores transparentes removibles para adultos',
          duration: '12-18 meses',
          price: 'Desde $300.000',
          features: ['Completamente invisible', 'Removible para comer', 'Sin brackets']
        }
      ]
    },
    {
      category: 'Implantes Dentales',
      icon: '🦿',
      services: [
        {
          name: 'Implante Unitario',
          description: 'Reemplazo de un diente faltante con implante de titanio',
          duration: '3-6 meses',
          price: 'Desde $800.000',
          features: ['Titanio de grado médico', 'Garantía de por vida', 'Cirugía mínimamente invasiva']
        },
        {
          name: 'Implantes Múltiples',
          description: 'Reemplazo de varios dientes con implantes estratégicos',
          duration: '4-8 meses',
          price: 'Desde $1.500.000',
          features: ['Planificación 3D', 'Cirugía guiada', 'Recuperación rápida']
        },
        {
          name: 'Rehabilitación Completa',
          description: 'Reemplazo de todos los dientes con implantes',
          duration: '6-12 meses',
          price: 'Desde $3.000.000',
          features: ['Diseño personalizado', 'Funcionalidad completa', 'Estética natural']
        }
      ]
    },
    {
      category: 'Estética Dental',
      icon: '✨',
      services: [
        {
          name: 'Blanqueamiento Profesional',
          description: 'Blanqueamiento en consultorio con tecnología LED',
          duration: '1 sesión',
          price: 'Desde $80.000',
          features: ['Resultados inmediatos', 'Sin sensibilidad', 'Dura hasta 2 años']
        },
        {
          name: 'Carillas de Porcelana',
          description: 'Láminas ultra-delgadas para mejorar la apariencia',
          duration: '2-3 sesiones',
          price: 'Desde $250.000',
          features: ['Diseño personalizado', 'Resistente y duradero', 'Apariencia natural']
        },
        {
          name: 'Bonding Dental',
          description: 'Reparación estética de dientes dañados o manchados',
          duration: '1 sesión',
          price: 'Desde $60.000',
          features: ['Procedimiento rápido', 'Sin anestesia', 'Resultado inmediato']
        }
      ]
    },
    {
      category: 'Odontología General',
      icon: '🏥',
      services: [
        {
          name: 'Limpieza Profunda',
          description: 'Limpieza profesional y eliminación de sarro',
          duration: '1 sesión',
          price: 'Desde $40.000',
          features: ['Eliminación de manchas', 'Prevención de caries', 'Encías saludables']
        },
        {
          name: 'Empastes Estéticos',
          description: 'Restauraciones con resina del color del diente',
          duration: '1 sesión',
          price: 'Desde $50.000',
          features: ['Material biocompatible', 'Apariencia natural', 'Durabilidad alta']
        },
        {
          name: 'Tratamiento de Conducto',
          description: 'Endodoncia para salvar dientes dañados',
          duration: '2-3 sesiones',
          price: 'Desde $120.000',
          features: ['Tecnología microscópica', 'Sin dolor', 'Preserva el diente']
        }
      ]
    },
    {
      category: 'Cirugía Oral',
      icon: '⚕️',
      services: [
        {
          name: 'Extracción Simple',
          description: 'Extracción de dientes en buen estado',
          duration: '1 sesión',
          price: 'Desde $80.000',
          features: ['Procedimiento rápido', 'Recuperación fácil', 'Post-operatorio simple']
        },
        {
          name: 'Extracción de Muelas del Juicio',
          description: 'Extracción de cordales impactados o problemáticos',
          duration: '1 sesión',
          price: 'Desde $150.000',
          features: ['Cirugía especializada', 'Sedación consciente', 'Seguimiento post-operatorio']
        },
        {
          name: 'Cirugía de Encías',
          description: 'Tratamiento de enfermedades periodontales avanzadas',
          duration: '2-4 sesiones',
          price: 'Desde $200.000',
          features: ['Técnicas mínimamente invasivas', 'Regeneración tisular', 'Resultados duraderos']
        }
      ]
    }
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const carouselImagesDesktop = [
    {
      url: '/serviciosAddi.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    }
  ];

  const carouselImagesMobile = [
    {
      url: '/serviciosAddiMobile.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', md: '100vw' },
          marginLeft: { xs: 0, md: 'calc(50% - 50vw)' },
          marginRight: { xs: 0, md: 'calc(50% - 50vw)' },
          borderRadius: { xs: '10px', md: 0 },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Image that adjusts to its natural height */}
        <Box
          component="img"
          src={isMobile ? carouselImagesMobile[0].url : carouselImagesDesktop[0].url}
          alt="Servicios"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            objectFit: 'cover'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Services by Category */}
        {services.map((category, categoryIndex) => (
          <Box key={categoryIndex} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ mr: 2 }}>
                {category.icon}
              </Typography>
              <Typography variant="h4" component="h2">
                {category.category}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {category.services.map((service, serviceIndex) => (
                <Card 
                  key={serviceIndex} 
                  sx={{ 
                    flex: '1 1 350px',
                    maxWidth: 400,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" component="h3" gutterBottom color="primary">
                      {service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<Schedule />} 
                        label={service.duration} 
                        size="small" 
                        variant="outlined" 
                      />
                      <Chip 
                        icon={<Star />} 
                        label={service.price} 
                        size="small" 
                        color="primary" 
                      />
                    </Box>

                    <List dense>
                      {service.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      component={RouterLink}
                      to="/citas"
                      variant="contained"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Agendar Cita
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        ))}

        {/* Additional Information */}
        <Box sx={{ backgroundColor: 'grey.50', p: 4, borderRadius: 2, mt: 6 }}>
          <Typography variant="h5" component="h3" gutterBottom textAlign="center">
            ¿No encuentras el servicio que buscas?
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
            Contáctanos para una consulta personalizada. Nuestro equipo evaluará tu caso 
            y te recomendará el mejor tratamiento para tus necesidades.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/contacto"
              variant="outlined"
              size="large"
              startIcon={<LocalHospital />}
            >
              Consultar Tratamiento
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Services; 